import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import {
  ArrowRight,
  Star,
  CalendarCheck,
  Trophy,
  ShieldCheck,
  GraduationCap,
  RotateCcw,
  Briefcase,
  Users2,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import { FeaturedAchievements } from "@/components/site/FeaturedAchievements";
import { TestimonialsSection } from "@/components/ui/testimonials-section";
import { TestimonialsCarousel } from "@/components/site/TestimonialsCarousel";
import blob from "@/assets/blob-accent.webp";

export const Route = createFileRoute("/achievements")({
  head: () => ({
    meta: [
      { title: "Student Achievements & Visa Wins - WeLivdIt" },
      {
        name: "description",
        content:
          "Real US visa approvals from WeLivdIt students. Watch the wins, read the stories, and see the 423+ verified F-1 approvals (350 Fall 2026 + 73 Spring/Fall 2027).",
      },
      { property: "og:title", content: "Student Achievements & Visa Wins - WeLivdIt" },
      {
        property: "og:description",
        content:
          "Real US visa approvals from WeLivdIt students. Watch the wins and see the 423+ verified F-1 approvals (350 Fall 2026 + 73 Spring/Fall 2027).",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://welivdit.com/achievements" },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://welivdit.com/achievements" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "CollectionPage",
          name: "Student Achievements - WeLivdIt",
          description:
            "Verified US visa approval stories from WeLivdIt students - F-1 freshers, 214(b) recoveries, and B1/B2 visitor visa wins.",
          url: "https://welivdit.com/achievements",
          isPartOf: { "@type": "WebSite", name: "WeLivdIt", url: "https://welivdit.com" },
          about: [
            { "@type": "Thing", name: "F-1 student visa approvals" },
            { "@type": "Thing", name: "214(b) refusal recoveries" },
            { "@type": "Thing", name: "B1/B2 visitor visa approvals" },
          ],
          mainEntity: {
            "@type": "ItemList",
            name: "WeLivdIt student US visa approvals",
            itemListOrder: "https://schema.org/ItemListOrderDescending",
            numberOfItems: 423,
          },
        }),
      },
    ],
  }),
  component: AchievementsPage,
});

const stats = [
  { icon: CalendarCheck, label: "Verified F-1 approvals · 350 Fall 2026 + 73 Spring/Fall 2027", value: "423+" },
  { icon: Star, label: "Trustpilot rating", value: "4.7 / 5" },
  { icon: Trophy, label: "F-1 refusal recoveries · Apr–July 2026 and counting", value: "40+" },
  { icon: ShieldCheck, label: "Years coaching", value: "5+" },
];

const categoryCards = [
  {
    id: "f1-fresher",
    icon: GraduationCap,
    tag: "F-1 Freshers",
    tagTone: "bg-primary/10 text-primary border-primary/20",
    title: "First-attempt F-1 approvals",
    stat: "1100+",
    desc: "Undergrad & MS students approved on their very first interview.",
    points: ["DS-160 fully reviewed", "2 mock interviews", "SEVIS + funds coaching"],
  },
  {
    id: "f1-refusal",
    icon: RotateCcw,
    tag: "F-1 · 214(b) Refusals",
    tagTone: "bg-amber-500/10 text-amber-700 dark:text-amber-400 border-amber-500/20",
    title: "F-1 refusal recoveries",
    stat: "400+",
    desc: "Students re-approved after a 214(b) refusal — many on the very next attempt.",
    points: ["Refusal root-cause audit", "Story restructuring", "Targeted mocks"],
  },
  {
    id: "b1-b2",
    icon: Briefcase,
    tag: "B1 / B2 Visitor",
    tagTone: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border-emerald-500/20",
    title: "Visitor & business visa prep",
    stat: "Mock Prep",
    desc: "End-to-end interview coaching for parents, spouses, and business travellers.",
    points: ["Ties-to-home script", "Sponsor docs review", "Mock + Q&A"],
  },
  {
    id: "f2-j1",
    icon: Users2,
    tag: "F2 / J1 Dependents",
    tagTone: "bg-sky-500/10 text-sky-700 dark:text-sky-400 border-sky-500/20",
    title: "Dependent & exchange visa prep",
    stat: "Mock Prep",
    desc: "Coaching for F2 dependents and J1 exchange visitors — DS-160 + mocks.",
    points: ["Relationship & intent script", "DS-160 walkthrough", "Mock interview"],
  },
];

function AchievementsPage() {
  const [activeCategory, setActiveCategory] = useState<string>("all");

  const filteredCards = useMemo(() => {
    if (activeCategory === "all") return categoryCards;
    return categoryCards.filter((card) => card.id === activeCategory);
  }, [activeCategory]);

  return (
    <main className="min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative overflow-hidden border-b border-border/60 bg-gradient-hero">
        <img
          src={blob}
          alt=""
          aria-hidden
          className="pointer-events-none absolute -top-40 -left-32 w-[600px] opacity-60 blur-2xl"
        />
        <div className="mx-auto max-w-6xl px-5 pt-16 pb-16 md:pt-24 md:pb-20 text-center relative">
          <div className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-3.5 py-1 text-xs font-semibold uppercase tracking-wider text-primary border border-primary/20 mb-4">
            <Sparkles className="h-3.5 w-3.5" /> Wins, Not Promises
          </div>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-foreground text-balance leading-[1.05]">
            Real students. Real <em className="not-italic text-primary">approvals.</em>
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-muted-foreground text-base sm:text-lg leading-relaxed">
            <span className="font-semibold text-foreground">423+ verified F-1 approvals</span> (350 Fall 2026 + 73
            Spring/Fall 2027) and 1,500+ since 2021. Every video, post, and review here is from a student who walked
            in prepared with us — and walked out approved.
          </p>
        </div>
      </section>

      {/* Stats Counter Bar */}
      <section className="mx-auto max-w-6xl px-5 -mt-8 md:-mt-10 relative z-10">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          {stats.map((s) => (
            <div
              key={s.label}
              className="rounded-2xl bg-card border border-border p-5 shadow-sm transition hover:border-primary/40 hover:-translate-y-0.5"
            >
              <div className="h-10 w-10 rounded-xl bg-primary/10 grid place-items-center text-primary">
                <s.icon className="h-5 w-5" />
              </div>
              <div className="mt-3 font-display text-2xl md:text-3xl font-bold text-foreground">{s.value}</div>
              <div className="text-[11px] font-medium text-muted-foreground mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Category Highlights Section with Interactive Filter */}
      <section className="mx-auto max-w-6xl px-5 mt-16 md:mt-24">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
          <div className="max-w-xl">
            <p className="text-xs font-bold uppercase tracking-wider text-primary">Highlights</p>
            <h2 className="mt-2 font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground">
              Wins across every applicant track.
            </h2>
            <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
              From first-time F-1 freshers to 214(b) refusal comebacks — here&apos;s where our students win the most.
            </p>
          </div>

          {/* Interactive Category Filter Pills */}
          <div className="flex flex-wrap gap-2">
            {[
              { id: "all", label: "All Wins" },
              { id: "f1-fresher", label: "F-1 Freshers" },
              { id: "f1-refusal", label: "214(b) Refusals" },
              { id: "b1-b2", label: "B1/B2 Visitor" },
              { id: "f2-j1", label: "F2/J1 Dependents" },
            ].map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveCategory(tab.id)}
                className={`px-3.5 py-1.5 text-xs rounded-full border transition-all font-medium ${
                  activeCategory === tab.id
                    ? "bg-primary text-primary-foreground border-primary shadow-sm"
                    : "bg-card text-muted-foreground border-border hover:border-primary/50"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Category Cards Grid */}
        <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {filteredCards.map((c) => (
            <div
              key={c.title}
              className="group rounded-3xl bg-card border border-border p-6 shadow-sm hover:border-primary/40 hover:shadow-md hover:-translate-y-1 transition-all duration-200 flex flex-col justify-between"
            >
              <div>
                <div className="flex items-center justify-between gap-2">
                  <div className="h-11 w-11 rounded-2xl bg-accent grid place-items-center text-primary shrink-0">
                    <c.icon className="h-5 w-5" />
                  </div>
                  <span
                    className={`text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border ${c.tagTone}`}
                  >
                    {c.tag}
                  </span>
                </div>

                <div className="mt-5 font-display text-3xl font-bold text-foreground">{c.stat}</div>
                <h3 className="mt-1 font-display text-lg font-bold text-foreground">{c.title}</h3>
                <p className="mt-2 text-xs text-muted-foreground leading-relaxed">{c.desc}</p>
              </div>

              <ul className="mt-5 pt-4 border-t border-border/60 space-y-2">
                {c.points.map((p) => (
                  <li key={p} className="flex items-start gap-2 text-xs text-foreground/80">
                    <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 mt-0.5 shrink-0" />
                    <span>{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* Featured Video & LinkedIn Embed Component */}
      <FeaturedAchievements eyebrow="Approval of the week" heading="Watch a recent WeLivdIt win." />

      {/* WhatsApp Testimonial Gallery Carousel */}
      <section className="relative overflow-hidden bg-marquee-banner mt-16 md:mt-24">
        <div className="mx-auto w-full max-w-7xl px-3 sm:px-6 lg:px-8 py-8 md:py-12">
          <div className="max-w-2xl mb-6">
            <p className="text-xs font-bold uppercase tracking-wider text-primary">Straight from WhatsApp</p>
            <h2 className="mt-2 font-display text-2xl sm:text-3xl md:text-4xl font-bold tracking-tight text-white">
              Approval messages from students.
            </h2>
          </div>
          <TestimonialsCarousel />
        </div>
      </section>

      {/* Trustpilot & Reviews Section */}
      <TestimonialsSection />

      {/* CTA Section */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:py-24">
        <div className="rounded-[2.5rem] bg-gradient-hero border border-border p-8 sm:p-12 md:p-16 text-center relative overflow-hidden">
          <img src={blob} alt="" aria-hidden className="absolute -bottom-40 -left-32 w-[500px] opacity-50 blur-3xl" />
          <div className="relative max-w-2xl mx-auto space-y-4">
            <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tight text-foreground text-balance">
              Be the next approval story.
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              Pick a plan that fits your visa journey and start prepping with our senior Specialists today.
            </p>

            <div className="pt-4 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/services"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-semibold shadow-glow hover:opacity-95 transition"
              >
                Enroll in our plans <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-border bg-card text-foreground font-semibold hover:bg-accent transition"
              >
                View Pricing
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
